import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import {
  Monitor,
  Plus,
  Search,
  Clock,
  Calendar,
  Image as ImageIcon,
  Trash2,
  ArrowRight,
  CheckCircle2,
  Layers,
} from 'lucide-react';

const SLOT_DURATIONS = [
  { value: 10, label: '10 seconds', description: 'Quick display' },
  { value: 15, label: '15 seconds', description: 'Standard' },
  { value: 30, label: '30 seconds', description: 'Extended' },
  { value: 60, label: '60 seconds', description: 'Premium' },
];

export default function ContentAssignmentPage() {
  const { user, isAdmin, isOperator } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [content, setContent] = useState([]);
  const [screens, setScreens] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [formData, setFormData] = useState({
    slot_duration: 15,
    start_date: '',
    end_date: '',
    start_time: '00:00',
    end_time: '23:59',
    priority: 1,
    campaign_id: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [assignmentsRes, contentRes, screensRes, campaignsRes] = await Promise.all([
        api.get('/content-assignments'),
        api.get('/content?approval_status=approved'),
        api.get('/screens'),
        api.get('/campaigns'),
      ]);
      setAssignments(assignmentsRes.data);
      setContent(contentRes.data.filter(c => c.approval_status === 'approved'));
      setScreens(screensRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedContent) {
      toast.error('Please select content');
      return;
    }
    if (selectedScreens.length === 0) {
      toast.error('Please select at least one screen');
      return;
    }

    try {
      await api.post('/content-assignments', {
        content_id: selectedContent.id,
        screen_ids: selectedScreens,
        ...formData,
      });
      toast.success('Content assigned successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleDelete = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    try {
      await api.delete(`/content-assignments/${assignmentId}`);
      toast.success('Assignment deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete assignment');
    }
  };

  const resetForm = () => {
    setSelectedContent(null);
    setSelectedScreens([]);
    setFormData({
      slot_duration: 15,
      start_date: '',
      end_date: '',
      start_time: '00:00',
      end_time: '23:59',
      priority: 1,
      campaign_id: '',
    });
  };

  const toggleScreen = (screenId) => {
    setSelectedScreens(prev => 
      prev.includes(screenId) 
        ? prev.filter(id => id !== screenId)
        : [...prev, screenId]
    );
  };

  const selectAllScreens = () => {
    if (selectedScreens.length === screens.length) {
      setSelectedScreens([]);
    } else {
      setSelectedScreens(screens.map(s => s.id));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin && !isOperator) {
    return (
      <div className="glass-card rounded-xl p-12 text-center">
        <Layers className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
        <h3 className="text-xl font-semibold text-white mb-2">Access Restricted</h3>
        <p className="text-zinc-400">Only operators and administrators can manage content assignments.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-assignment-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Content Assignment
          </h1>
          <p className="text-zinc-400 mt-1">Assign content to screens with time slots</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-500 btn-glow"
          data-testid="create-assignment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Assignment
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Layers className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  {assignments.length}
                </p>
                <p className="text-sm text-zinc-400">Active Assignments</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  {content.length}
                </p>
                <p className="text-sm text-zinc-400">Approved Content</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Monitor className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  {screens.filter(s => s.status === 'online').length}
                </p>
                <p className="text-sm text-zinc-400">Online Screens</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assignments Table */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Current Assignments
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {assignments.length === 0 ? (
            <div className="p-12 text-center">
              <Layers className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold text-white mb-2">No assignments yet</h3>
              <p className="text-zinc-400 mb-6">Create your first content assignment to get started</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-500">
                <Plus className="w-4 h-4 mr-2" />
                New Assignment
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Content</TableHead>
                  <TableHead className="text-zinc-500">Screens</TableHead>
                  <TableHead className="text-zinc-500">Slot Duration</TableHead>
                  <TableHead className="text-zinc-500">Schedule</TableHead>
                  <TableHead className="text-zinc-500">Priority</TableHead>
                  <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map((assignment) => (
                  <TableRow key={assignment.id} className="border-zinc-800">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-zinc-500" />
                        </div>
                        <span className="text-white font-medium">{assignment.content_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {assignment.screen_names.slice(0, 2).map((name, i) => (
                          <Badge key={i} variant="outline" className="bg-zinc-800 border-zinc-700 text-xs">
                            {name}
                          </Badge>
                        ))}
                        {assignment.screen_names.length > 2 && (
                          <Badge variant="outline" className="bg-zinc-800 border-zinc-700 text-xs">
                            +{assignment.screen_names.length - 2} more
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                        <Clock className="w-3 h-3 mr-1" />
                        {assignment.slot_duration}s
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm">
                      {assignment.start_date} - {assignment.end_date}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                        P{assignment.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-500 hover:text-rose-400"
                        onClick={() => handleDelete(assignment.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Assignment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Create Content Assignment
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: Select Content */}
            <div>
              <Label className="text-zinc-300 text-base font-semibold">1. Select Content</Label>
              <p className="text-sm text-zinc-500 mb-3">Choose approved content to display</p>
              
              {content.length === 0 ? (
                <div className="p-6 rounded-lg bg-zinc-800/50 text-center">
                  <p className="text-zinc-400">No approved content available</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 max-h-[200px] overflow-y-auto">
                  {content.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => setSelectedContent(item)}
                      className={`cursor-pointer rounded-lg border-2 transition-all ${
                        selectedContent?.id === item.id
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <div className="aspect-video bg-zinc-800 rounded-t-lg flex items-center justify-center">
                        {item.thumbnail_url || item.url ? (
                          <img src={item.thumbnail_url || item.url} alt={item.name} className="w-full h-full object-cover rounded-t-lg" />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-zinc-600" />
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-sm text-white truncate">{item.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Step 2: Select Screens */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-zinc-300 text-base font-semibold">2. Select Screens</Label>
                  <p className="text-sm text-zinc-500">Choose which screens will display this content</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={selectAllScreens} className="border-zinc-700">
                  {selectedScreens.length === screens.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-2 max-h-[150px] overflow-y-auto">
                {screens.map((screen) => (
                  <div
                    key={screen.id}
                    onClick={() => toggleScreen(screen.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-all ${
                      selectedScreens.includes(screen.id)
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <Checkbox checked={selectedScreens.includes(screen.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{screen.name}</p>
                      <p className="text-xs text-zinc-500 truncate">{screen.location}</p>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`text-[10px] ${
                        screen.status === 'online' ? 'status-online' : 
                        screen.status === 'offline' ? 'status-offline' : 'status-maintenance'
                      }`}
                    >
                      {screen.status}
                    </Badge>
                  </div>
                ))}
              </div>
              <p className="text-sm text-zinc-500 mt-2">{selectedScreens.length} screen(s) selected</p>
            </div>

            {/* Step 3: Configure Slot */}
            <div>
              <Label className="text-zinc-300 text-base font-semibold">3. Configure Display Settings</Label>
              <p className="text-sm text-zinc-500 mb-3">Set duration, schedule, and link to campaign</p>
              
              {/* Campaign Selection */}
              <div className="mb-4">
                <Label className="text-zinc-400 text-sm">Campaign (Optional)</Label>
                <Select
                  value={formData.campaign_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, campaign_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="campaign-select">
                    <SelectValue placeholder="Select campaign" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="none">No Campaign</SelectItem>
                    {campaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name} ({campaign.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500 mt-1">Link this assignment to a campaign for tracking</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-sm">Slot Duration</Label>
                  <Select
                    value={formData.slot_duration.toString()}
                    onValueChange={(value) => setFormData({ ...formData, slot_duration: parseInt(value) })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="slot-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {SLOT_DURATIONS.map((slot) => (
                        <SelectItem key={slot.value} value={slot.value.toString()}>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {slot.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-zinc-400 text-sm">Priority (1-10)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-zinc-400 text-sm">Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    required
                  />
                </div>

                <div>
                  <Label className="text-zinc-400 text-sm">End Date</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    required
                  />
                </div>

                <div>
                  <Label className="text-zinc-400 text-sm">Daily Start Time</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-zinc-400 text-sm">Daily End Time</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500" data-testid="save-assignment-btn">
                <ArrowRight className="w-4 h-4 mr-2" />
                Create Assignment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
